import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { FiTrendingUp } from 'react-icons/fi';
import {
  CHART_PALETTE,
  CHART_FONT,
  CHART_GRID,
  CHART_CATEGORY_AXIS,
  CHART_TOOLTIP,
  CHART_LEGEND,
  CHART_SEMANTIC
} from 'helpers/echartsTheme';

const BAR_PIPELINE = CHART_PALETTE[0];
const BAR_SIGNED   = CHART_SEMANTIC.success;
const LINE_COUNT   = CHART_PALETTE[2];

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const buildOption = (items) => ({
  textStyle: { fontFamily: CHART_FONT.fontFamily },
  grid: { ...CHART_GRID, top: 36, bottom: 0 },
  legend: {
    ...CHART_LEGEND,
    top: 0,
    left: 0,
    data: ['Pipeline Value', 'Signed Value', 'Project Count']
  },
  tooltip: {
    ...CHART_TOOLTIP,
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    formatter: (params) => {
      const idx  = params?.[0]?.dataIndex;
      const item = items[idx];
      if (!item) return '';
      return [
        `<div style="font-weight:700;color:#111827">${item.period_label}</div>`,
        `<div style="font-size:11px;color:#6b7280">${item.project_count} projects</div>`,
        `<div style="font-size:14px;font-weight:800;color:${BAR_PIPELINE}">${fmtM(item.total_value)}</div>`,
        `<div style="font-size:11px;color:${BAR_SIGNED}">Signed: ${fmtM(item.contract_value)}</div>`,
        `<div style="font-size:11px;color:${CHART_SEMANTIC.danger}">Lost: ${item.lost_count}</div>`
      ].join('');
    }
  },
  xAxis: {
    type: 'category',
    ...CHART_CATEGORY_AXIS,
    data: items.map((d) => d.period_label),
    axisLabel: { ...CHART_CATEGORY_AXIS.axisLabel, fontSize: 9, interval: 0 }
  },
  yAxis: [
    { type: 'value', show: false },
    { type: 'value', show: false }
  ],
  series: [
    {
      name: 'Pipeline Value',
      type: 'bar',
      barWidth: 40,
      z: 1,
      itemStyle: { color: BAR_PIPELINE, opacity: 0.75, borderRadius: [3, 3, 0, 0] },
      data: items.map((d) => d.total_value)
    },
    {
      name: 'Signed Value',
      type: 'bar',
      barWidth: 40,
      barGap: '-100%',
      z: 2,
      itemStyle: { color: BAR_SIGNED, opacity: 0.85, borderRadius: [3, 3, 0, 0] },
      data: items.map((d) => d.contract_value)
    },
    {
      name: 'Project Count',
      type: 'line',
      yAxisIndex: 1,
      z: 3,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: { color: LINE_COUNT, borderColor: '#ffffff', borderWidth: 2 },
      lineStyle: { color: LINE_COUNT, width: 1.5, type: 'dashed', opacity: 0.7 },
      data: items.map((d) => d.project_count)
    }
  ]
});

const TrendChart = ({ data, loading, error, groupBy, onGroupByChange }) => {
  if (loading) return (
    <Card className="tw-p-6 tw-border tw-border-gray-200">
      <div className="tw-flex tw-justify-center tw-py-12"><LoadingSpinner size="lg" /></div>
    </Card>
  );
  if (error) return (
    <Card className="tw-p-4 tw-bg-red-50 tw-border tw-border-red-200">
      <p className="tw-text-red-600 tw-text-sm">Error loading trend data</p>
    </Card>
  );

  const items = data || [];

  return (
    <Card className="tw-border tw-border-gray-200 tw-bg-gradient-to-br tw-from-sky-50 tw-via-white tw-to-teal-50 tw-shadow-sm">
      <div className="tw-p-5 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between">
        <div>
          <h3 className="tw-font-bold tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
            <FiTrendingUp className="tw-text-sky-600" size={18} />
            Trend Analysis
          </h3>
          <p className="tw-text-xs tw-text-gray-500 tw-mt-0.5">Project count and value over time</p>
        </div>
        <div className="tw-flex tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
          {['month', 'quarter', 'year'].map((g) => (
            <button
              key={g}
              onClick={() => onGroupByChange(g)}
              className={`tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors tw-cursor-pointer ${
                groupBy === g ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-white tw-text-gray-600 hover:tw-bg-gray-50'
              }`}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="tw-p-5 tw-overflow-x-auto">
        {items.length === 0 ? (
          <p className="tw-text-gray-400 tw-text-center tw-py-8">No trend data available</p>
        ) : (
          <ReactECharts
            option={buildOption(items)}
            style={{ height: 230, width: '100%', minWidth: Math.max(items.length * 60 + 20, 300) }}
            notMerge
          />
        )}
      </div>
    </Card>
  );
};

TrendChart.propTypes = {
  data:            PropTypes.array,
  loading:         PropTypes.bool,
  error:           PropTypes.any,
  groupBy:         PropTypes.oneOf(['month', 'quarter', 'year']),
  onGroupByChange: PropTypes.func
};
TrendChart.defaultProps = { data: [], loading: false, error: null, groupBy: 'month', onGroupByChange: () => {} };

export default TrendChart;
